import { useMemo, useState } from "react";
import axios from "axios";
import {
  X, AlertTriangle, CheckCircle2, Loader2, PenLine, ClipboardList,
  Package, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { unitCompletionWarnings, derivedStatus } from "@/lib/unitStatus";
import { formatQty } from "@/lib/materials";
import SignatureCanvas from "@/components/SignatureCanvas";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND}/api`;

/**
 * FinalizeReviewDialog — "Raportin viimeistely" -näkymä kenttätyöntekijälle
 * kun "Merkitse koko kohde valmiiksi" painetaan.
 *
 * Käyttäjän spec:
 *  - Näyttää PUUTTEET (varoitukset) ylhäällä — pelkkiä muistutuksia, ei blokkaa.
 *  - Vapaa tekstikenttä: "Tehdyt toimenpiteet / lisätiedot koko kohteesta"
 *    (tallennetaan `ai_summary`-kenttään backendillä).
 *  - Aggregoi HAVAINNOT kaikista yksiköistä (yksikkökohtaisesti listattuna):
 *    kunkin yksikön ruksatut finding-checklist-kohdat + additional_work_text.
 *  - Aggregoi TARVIKKEET kaikista yksiköistä (yksikkökohtaisesti listattuna).
 *  - Ei AI:ta — ei ehdotuksia, ei kirjoittamista käyttäjän puolesta.
 *  - "Lähetä raportti" -painike viimeistelee: bulk-merkkaa yksiköt valmiiksi,
 *    tallentaa yhteenvedon, tallentaa allekirjoituksen jos pyydetty.
 */
export default function FinalizeReviewDialog({ report, editToken, onClose, onCompleted }) {
  const [phase, setPhase] = useState("review"); // review | signing | finalizing
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(report.ai_summary || "");
  const [sigData, setSigData] = useState(null);
  const [sigName, setSigName] = useState(report.signature_name || "");

  const needsSignature = !!report.request_signature;

  // Kootaan varoitukset ja aggregaatit muistiin — vältetään turhaa uudelleen-
  // laskentaa jos käyttäjä kirjoittaa summary-kenttään.
  const { warnings, findingsByUnit, materialsByUnit, hasAnyFindings, hasAnyMaterials } = useMemo(() => {
    const wOut = [];
    const fOut = [];
    const mOut = [];
    for (const u of report.units || []) {
      const wl = unitCompletionWarnings(u);
      if (wl.length > 0) wOut.push({ unit: u, warnings: wl });

      const findingChecks = (u.checklist || [])
        .filter((c) => c.checked && c.is_finding)
        .map((c) => c.label);
      const extraText = (u.additional_work_text || "").trim();
      if (findingChecks.length > 0 || extraText) {
        fOut.push({ id: u.id, label: u.label, checks: findingChecks, text: extraText });
      }

      const mats = u.materials_used || [];
      if (mats.length > 0) mOut.push({ id: u.id, label: u.label, items: mats });
    }
    return {
      warnings: wOut,
      findingsByUnit: fOut,
      materialsByUnit: mOut,
      hasAnyFindings: fOut.length > 0,
      hasAnyMaterials: mOut.length > 0,
    };
  }, [report.units]);

  const proceed = async () => {
    // Jos allekirjoitus vaaditaan, siirry ensin allekirjoitusvaiheeseen.
    if (needsSignature && phase === "review") {
      setPhase("signing");
      return;
    }
    await finalize();
  };

  const finalize = async () => {
    setBusy(true);
    setPhase("finalizing");
    try {
      // 1. Tallenna yhteenveto (aina — myös tyhjänä ok, ei AI-generointia)
      if (summary !== (report.ai_summary || "")) {
        try {
          await axios.patch(`${API_BASE}/reports/edit/${editToken}/summary`, {
            ai_summary: summary,
          });
        } catch {
          // best-effort — ei blokata jos serveri ei ota tätä
        }
      }
      // 2. Allekirjoitus (jos jotain piirretty tai nimi kirjoitettu)
      if (needsSignature && (sigData || (sigName || "").trim())) {
        try {
          await axios.patch(`${API_BASE}/reports/edit/${editToken}/signature`, {
            signature_data: sigData,
            signature_name: (sigName || "").trim() || null,
          });
        } catch {
          // best-effort
        }
      }
      // 3. Bulk-merkkaa avoimet/keskeneräiset yksiköt valmiiksi
      const toUpdate = (report.units || []).filter(
        (u) => derivedStatus(u) !== "done" && derivedStatus(u) !== "inaccessible",
      );
      for (const u of toUpdate) {
        try {
          await axios.patch(
            `${API_BASE}/reports/edit/${editToken}/units/${u.id}`,
            { status: "done" },
          );
        } catch {
          // yksittäisen yksikön virhe ei blokkaa muita
        }
      }
      toast.success("Raportti lähetetty");
      onCompleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Viimeistely epäonnistui");
      setPhase(needsSignature ? "signing" : "review");
    } finally {
      setBusy(false);
    }
  };

  const skipSignature = async () => {
    setSigData(null);
    setSigName("");
    await finalize();
  };

  // -- SIGNING PHASE --
  if (phase === "signing") {
    return (
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6 bg-black/40" data-testid="finalize-signature">
        <div className="bg-white w-full md:max-w-lg rounded-t-2xl md:rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <div className="flex items-center gap-2.5">
              <PenLine className="h-6 w-6 text-emerald-600 flex-shrink-0" />
              <h3 className="font-display text-lg font-semibold text-slate-900">Allekirjoita raportti</h3>
            </div>
            <button
              onClick={() => setPhase("review")}
              className="p-2 rounded-lg hover:bg-slate-100"
              data-testid="finalize-signature-back"
              aria-label="Takaisin"
            >
              <ArrowLeft className="h-5 w-5 text-slate-400" />
            </button>
          </div>
          <div className="p-5 overflow-y-auto flex-1 space-y-4">
            <p className="text-sm text-slate-600">
              Allekirjoita raportti sormella tai hiirellä. Voit halutessasi myös ohittaa.
            </p>
            <SignatureCanvas onChange={setSigData} />
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                Nimenselvennys
              </label>
              <input
                type="text"
                value={sigName}
                onChange={(e) => setSigName(e.target.value)}
                placeholder="esim. Mikko Mallikas"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-400 outline-none text-sm"
                data-testid="finalize-signature-name"
              />
            </div>
          </div>
          <div className="p-5 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-2 justify-end">
            <button
              type="button"
              onClick={skipSignature}
              disabled={busy}
              className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50"
              data-testid="finalize-signature-skip"
            >
              Ohita allekirjoitus
            </button>
            <button
              type="button"
              onClick={finalize}
              disabled={busy || (!sigData && !(sigName || "").trim())}
              className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
              data-testid="finalize-signature-confirm"
            >
              Tallenna ja lähetä
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -- FINALIZING PHASE (progress spinner) --
  if (phase === "finalizing") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="finalize-progress">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <Loader2 className="h-8 w-8 text-emerald-600 animate-spin mx-auto" />
          <div className="font-display text-lg font-semibold text-slate-900 mt-3">Lähetetään raporttia…</div>
          <div className="text-sm text-slate-500 mt-1">Tallennetaan yksiköt ja yhteenveto.</div>
        </div>
      </div>
    );
  }

  // -- REVIEW PHASE --
  return (
    <div className="fixed inset-0 z-50 bg-slate-50 md:bg-black/40 flex md:items-center md:justify-center md:p-6" data-testid="finalize-review">
      <div className="bg-slate-50 md:bg-white w-full md:max-w-2xl md:rounded-2xl md:shadow-xl md:max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="bg-white sticky top-0 md:static border-b border-slate-100 px-4 md:px-5 py-3 md:py-4 flex items-center gap-3 md:rounded-t-2xl z-10">
          <button
            onClick={onClose}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-500"
            aria-label="Takaisin"
            data-testid="finalize-close"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Viimeistely</div>
            <h2 className="font-display text-lg font-semibold text-slate-900 truncate">Raportin viimeistely</h2>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5 space-y-6 pb-32">
          {/* Puutteet */}
          {warnings.length > 0 && (
            <section data-testid="finalize-warnings">
              <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-2 inline-flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> Puutteita ({warnings.length})
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-3 space-y-2">
                {warnings.map(({ unit, warnings: wl }) => (
                  <div key={unit.id} className="text-sm" data-testid={`finalize-warn-${unit.id}`}>
                    <div className="font-semibold text-slate-900">{unit.label}</div>
                    <ul className="mt-0.5 space-y-0.5">
                      {wl.map((w, i) => (
                        <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
                          <span className="text-amber-500 mt-0.5">•</span>
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <div className="text-[11px] text-amber-700 pt-1 border-t border-amber-200/70 mt-2">
                  Voit silti lähettää raportin — nämä ovat vain muistutuksia.
                </div>
              </div>
            </section>
          )}

          {/* Tehdyt toimenpiteet / lisätiedot koko kohteesta */}
          <section>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
              <ClipboardList className="h-3.5 w-3.5" /> Tehdyt toimenpiteet / lisätiedot
            </div>
            <textarea
              rows={5}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Kirjoita lyhyt yhteenveto koko kohteen työstä. Esim. tehdyt toimenpiteet, yleiset havainnot, huomiot tilaajalle…"
              className="w-full px-3 py-3 rounded-xl border border-slate-200 bg-white text-sm leading-relaxed focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
              data-testid="finalize-summary"
            />
            <div className="text-[11px] text-slate-500 mt-1.5">
              Tämä teksti näkyy raportin alussa. Yksikkökohtaiset kirjaukset säilyvät niiden omissa yksiköissä.
            </div>
          </section>

          {/* Havainnot — yksikkökohtaisesti koottuna */}
          <section>
            <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-2 inline-flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> Havainnot ({findingsByUnit.length})
            </div>
            {hasAnyFindings ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/40 divide-y divide-amber-200/70">
                {findingsByUnit.map((f) => (
                  <div key={f.id} className="p-3" data-testid={`finalize-findings-${f.id}`}>
                    <div className="font-semibold text-sm text-slate-900">{f.label}</div>
                    {f.checks.length > 0 && (
                      <ul className="mt-1 text-sm text-slate-800 list-disc list-inside space-y-0.5">
                        {f.checks.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    )}
                    {f.text && (
                      <div className="mt-1 text-sm text-slate-800 whitespace-pre-line leading-relaxed">{f.text}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-500 text-center">
                Ei havaintoja kirjattu.
              </div>
            )}
          </section>

          {/* Tarvikkeet — yksikkökohtaisesti koottuna */}
          <section>
            <div className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold mb-2 inline-flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5" /> Tarvikkeet
            </div>
            {hasAnyMaterials ? (
              <div className="rounded-2xl border border-blue-200 bg-blue-50/40 divide-y divide-blue-200/70">
                {materialsByUnit.map((mu) => (
                  <div key={mu.id} className="p-3" data-testid={`finalize-materials-${mu.id}`}>
                    <div className="font-semibold text-sm text-slate-900">{mu.label}</div>
                    <ul className="mt-1 text-sm text-slate-800 space-y-0.5">
                      {mu.items.map((m, i) => (
                        <li key={i} className="flex items-baseline gap-2 border-b border-dotted border-blue-200/60 py-0.5 last:border-0">
                          <span className="flex-1">{m.name}</span>
                          <span className="text-slate-500 whitespace-nowrap">{formatQty(m.qty)} {m.unit || "kpl"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-500 text-center">
                Ei tarvikkeita kirjattu.
              </div>
            )}
          </section>
        </div>

        {/* Footer — sticky */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 md:px-6 py-3 md:py-4 flex items-center gap-2 md:rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="finalize-cancel"
          >
            Palaa muokkaamaan
          </button>
          <button
            type="button"
            onClick={proceed}
            disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm disabled:opacity-50"
            data-testid="finalize-confirm"
          >
            <CheckCircle2 className="h-4 w-4" />
            {needsSignature ? "Jatka allekirjoitukseen" : "Lähetä raportti"}
          </button>
        </div>
      </div>
    </div>
  );
}
